import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, mergeProps as _mergeProps, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  class: "d-flex justify-space-between px-4 py-2",
  style: {"gap":"10px"}
}
const _hoisted_2 = { style: {"flex":"1"} }
const _hoisted_3 = {
  key: 0,
  class: "text-caption mb-1"
}
const _hoisted_4 = { class: "d-flex align-center" }
const _hoisted_5 = {
  key: 0,
  style: {"flex":"1"}
}
const _hoisted_6 = { class: "d-flex align-center" }
const _hoisted_7 = {
  key: 0,
  class: "px-4 pb-3"
}

import { ref, watch, computed } from 'vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'TimePickerInput',
  props: {
    modelValue: {},
    label: {},
    rules: {},
    range: { type: Boolean }
  },
  emits: ['update:modelValue'],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const isOpen = ref(false);

const internalStartTime = ref<string | null>(null);
const internalStartPeriod = ref<'AM' | 'PM'>('AM');
const internalEndTime = ref<string | null>(null);
const internalEndPeriod = ref<'AM' | 'PM'>('AM');

const range = computed(() => props.range !== false);

const durationPresets = [
  { label: '15 min', minutes: 15 },
  { label: '30 min', minutes: 30 },
  { label: '1 hora', minutes: 60 },
  { label: '1.5 horas', minutes: 90 },
  { label: '2 horas', minutes: 120 },
  /*{ label: '3h', minutes: 180 },
  { label: '4h', minutes: 240 },
  { label: '8h', minutes: 480 }*/
];

// Opciones en 5 min (ej: 08:00, 08:05, ...)
function pad(n: number) { return String(n).padStart(2, '0'); }
const timeOptions = Array.from({ length: 12 * 12 }, (_, i) => {
  const h = (i / 12) | 0;
  const m = (i % 12) * 5;
  return `${pad(h === 0 ? 12 : h)}:${pad(m)}`;
});

function toMinutes(val: string, period: 'AM' | 'PM') {
  if (!val) return -1;
  const [h, m] = val.split(':').map(Number);
  let hour = h === 12 ? 0 : h;
  if (period === 'PM') hour += 12;
  return hour * 60 + m;
}
function fromMinutes(mins: number) {
  const hour = ((Math.floor(mins / 60) % 12) || 12);
  const min = mins % 60;
  const period = mins < 720 ? 'AM' : 'PM';
  return { time: `${pad(hour)}:${pad(min)}`, period };
}

const endOptions = computed(() => {
  if (!internalStartTime.value) return timeOptions;
  const startMin = toMinutes(internalStartTime.value, internalStartPeriod.value);
  const filtered: string[] = [];
  for (let period of ['AM','PM'] as const) {
    for (let opt of timeOptions) {
      const min = toMinutes(opt, period);
      if (min > startMin) filtered.push(opt);
    }
  }
  return filtered;
});

// Acepta 12h ("06:15 PM") o 24h ("18:15:00") y lo normaliza a [hh:mm, 'AM'|'PM']
function parseTo12h(str: string): [string, string] {
  if (!str) return ['12:00', 'AM'];
  const match12 = str.match(/(\d{2}):(\d{2})\s*(AM|PM)/i);
  if (match12) {
    return [`${match12[1]}:${match12[2]}`, (match12[3] || 'AM').toUpperCase()];
  }
  // Si es formato 24h
  const match24 = str.match(/(\d{2}):(\d{2})/);
  if (match24) {
    let hour = parseInt(match24[1], 10);
    const min = match24[2];
    const period = hour >= 12 ? 'PM' : 'AM';
    if (hour === 0) hour = 12;
    else if (hour > 12) hour -= 12;
    return [`${String(hour).padStart(2, '0')}:${min}`, period];
  }
  return ['12:00', 'AM'];
}

watch(
  () => props.modelValue,
  (val) => {
    if (!val && !isOpen.value) {
      resetFields();
      return;
    }
    // Normaliza rango
    if (Array.isArray(val)) {
      const [start, end] = val;
      if (start) {
        const [sTime, sPeriod] = parseTo12h(start);
        internalStartTime.value = sTime;
        internalStartPeriod.value = sPeriod as 'AM' | 'PM';
      }
      if (end) {
        const [eTime, ePeriod] = parseTo12h(end);
        internalEndTime.value = eTime;
        internalEndPeriod.value = ePeriod as 'AM' | 'PM';
      }
    } else if (typeof val === 'string') {
      const [sTime, sPeriod] = parseTo12h(val);
      internalStartTime.value = sTime;
      internalStartPeriod.value = sPeriod as 'AM' | 'PM';
      internalEndTime.value = null;
      internalEndPeriod.value = 'AM';
    }
  },
  { immediate: true }
);


function resetFields() {
  internalStartTime.value = null;
  internalStartPeriod.value = 'AM';
  internalEndTime.value = null;
  internalEndPeriod.value = 'AM';
}

function parseTimeString(str: string): [string, string] {
  const match = str.match(/(\d{2}:\d{2})\s*(AM|PM)?/i);
  if (match) {
    return [match[1], (match[2] || 'AM').toUpperCase()];
  }
  return ['12:00', 'AM'];
}

function formatModel() {
  if (range.value) {
    if (internalStartTime.value && internalStartPeriod.value && internalEndTime.value && internalEndPeriod.value)
      return [`${internalStartTime.value} ${internalStartPeriod.value}`, `${internalEndTime.value} ${internalEndPeriod.value}`];
    return null;
  } else {
    if (internalStartTime.value && internalStartPeriod.value)
      return `${internalStartTime.value} ${internalStartPeriod.value}`;
    return '';
  }
}

// Ahora sí, nunca borra la inicial por accidente
function onStartChange(val: string) {
  if (range.value && internalEndTime.value) {
    const startMin = toMinutes(val, internalStartPeriod.value);
    const endMin = toMinutes(internalEndTime.value, internalEndPeriod.value);
    if (endMin <= startMin) {
      internalEndTime.value = null;
    }
  }
  emitModel();
}
function onStartPeriodChange(val: 'AM' | 'PM') {
  if (range.value && internalEndTime.value && internalStartTime.value) {
    const startMin = toMinutes(internalStartTime.value, val);
    const endMin = toMinutes(internalEndTime.value, internalEndPeriod.value);
    if (endMin <= startMin) {
      internalEndTime.value = null;
    }
  }
  emitModel();
}
function onEndChange() {
  emitModel();
}
function onEndPeriodChange() {
  emitModel();
}
function emitModel() {
  const out = formatModel();
  emit('update:modelValue', out);
}

// Presets
function setDurationPreset(preset: { label: string; minutes: number }) {
  if (!internalStartTime.value) return;
  const startMin = toMinutes(internalStartTime.value, internalStartPeriod.value);
  let endMin = startMin + preset.minutes;
  if (endMin >= 24 * 60) endMin = 24 * 60 - 5;
  const { time, period } = fromMinutes(endMin);
  internalEndTime.value = time;
  internalEndPeriod.value = period as 'AM' | 'PM';
  emitModel();
}
function isDurationSelected(preset: { label: string; minutes: number }) {
  if (!internalStartTime.value || !internalEndTime.value) return false;
  const start = toMinutes(internalStartTime.value, internalStartPeriod.value);
  const end = toMinutes(internalEndTime.value, internalEndPeriod.value);
  return end - start === preset.minutes;
}

const displayValue = computed(() => {
  if (!internalStartTime.value) return '';
  if (range.value && internalEndTime.value)
    return `${internalStartTime.value} ${internalStartPeriod.value} – ${internalEndTime.value} ${internalEndPeriod.value}`;
  return `${internalStartTime.value} ${internalStartPeriod.value}`;
});

function clearTime() {
  resetFields();
  emit('update:modelValue', range.value ? null : '');
}

return (_ctx: any,_cache: any) => {
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_autocomplete = _resolveComponent("v-autocomplete")!
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_divider = _resolveComponent("v-divider")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_chip_group = _resolveComponent("v-chip-group")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_menu = _resolveComponent("v-menu")!

  return (_openBlock(), _createBlock(_component_v_menu, {
    modelValue: isOpen.value,
    "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((isOpen).value = $event)),
    "close-on-content-click": false,
    transition: "scale-transition",
    "offset-y": "",
    "min-width": "500px"
  }, {
    activator: _withCtx(({ props: menuProps }) => [
      _createVNode(_component_v_text_field, _mergeProps(menuProps, {
        modelValue: displayValue.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((displayValue).value = $event)),
        label: _ctx.label,
        readonly: true,
        clearable: true,
        rules: _ctx.rules,
        "prepend-inner-icon": "mdi-clock-outline",
        density: "compact",
        "onClick:clear": clearTime,
        variant: "solo-filled"
      }), null, 16, ["modelValue", "label", "rules"])
    ]),
    default: _withCtx(() => [
      _createVNode(_component_v_card, null, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_1, [
            _createElementVNode("div", _hoisted_2, [
              (range.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_3, "Hora inicio"))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_4, [
                _createVNode(_component_v_autocomplete, {
                  modelValue: internalStartTime.value,
                  "onUpdate:modelValue": [
                    _cache[1] || (_cache[1] = ($event: any) => ((internalStartTime).value = $event)),
                    onStartChange
                  ],
                  items: _unref(timeOptions),
                  density: "compact",
                  "hide-details": "",
                  placeholder: "--:--",
                  style: {"flex":"1"},
                  variant: "solo-filled"
                }, null, 8, ["modelValue", "items"]),
                _createVNode(_component_v_select, {
                  modelValue: internalStartPeriod.value,
                  "onUpdate:modelValue": [
                    _cache[2] || (_cache[2] = ($event: any) => ((internalStartPeriod).value = $event)),
                    onStartPeriodChange
                  ],
                  items: ['AM','PM'],
                  density: "compact",
                  "hide-details": "",
                  style: {"max-width":"90px","margin-left":"6px"},
                  variant: "solo-filled"
                }, null, 8, ["modelValue"])
              ])
            ]),
            (range.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                  _cache[6] || (_cache[6] = _createElementVNode("div", { class: "text-caption mb-1" }, "Hora fin", -1)),
                  _createElementVNode("div", _hoisted_6, [
                    _createVNode(_component_v_autocomplete, {
                      modelValue: internalEndTime.value,
                      "onUpdate:modelValue": [
                        _cache[3] || (_cache[3] = ($event: any) => ((internalEndTime).value = $event)),
                        onEndChange
                      ],
                      items: endOptions.value,
                      density: "compact",
                      "hide-details": "",
                      placeholder: "--:--",
                      style: {"flex":"1"}
                    }, null, 8, ["modelValue", "items"]),
                    _createVNode(_component_v_select, {
                      modelValue: internalEndPeriod.value,
                      "onUpdate:modelValue": [
                        _cache[4] || (_cache[4] = ($event: any) => ((internalEndPeriod).value = $event)),
                        onEndPeriodChange
                      ],
                      items: ['AM','PM'],
                      density: "compact",
                      "hide-details": "",
                      style: {"max-width":"90px","margin-left":"6px"},
                      variant: "solo-filled"
                    }, null, 8, ["modelValue"])
                  ])
                ]))
              : _createCommentVNode("", true)
          ]),
          _createVNode(_component_v_divider, { class: "my-2" }),
          (range.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                _cache[7] || (_cache[7] = _createElementVNode("div", { class: "text-caption mb-2" }, "Duraciones rápidas:", -1)),
                _createVNode(_component_v_chip_group, {
                  class: "d-flex flex-wrap",
                  column: true
                }, {
                  default: _withCtx(() => [
                    (_openBlock(), _createElementBlock(_Fragment, null, _renderList(durationPresets, (preset) => {
                      return _createVNode(_component_v_chip, {
                        key: preset.label,
                        onClick: ($event: any) => (setDurationPreset(preset)),
                        color: isDurationSelected(preset) ? 'primary' : undefined,
                        class: "ma-1",
                        outlined: ""
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(preset.label), 1)
                        ]),
                        _: 2
                      }, 1032, ["onClick", "color"])
                    }), 64))
                  ]),
                  _: 1
                })
              ]))
            : _createCommentVNode("", true)
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["modelValue"]))
}
}

})